import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface WorkflowStatus {
  statusId: string;
  name: string;
  color: string;
  category: 'TODO' | 'IN_PROGRESS' | 'DONE';
  sortOrder: number;
}

export interface WorkflowProject {
  projectId: string;
  name: string;
  projectKey: string;
  projectType: string;
}

export interface WorkflowDetail {
  workflowId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  statusCount: number;
  projectCount: number;
  createdAt: string;
  updatedAt: string;
  statuses: WorkflowStatus[];
  projects: WorkflowProject[];
}

export interface StatusInput {
  statusId?: string;
  name: string;
  color: string;
  category: 'TODO' | 'IN_PROGRESS' | 'DONE';
  sortOrder: number;
}

export interface CreateWorkflowData {
  name: string;
  description?: string;
  statuses: StatusInput[];
}

export interface UpdateWorkflowData {
  name: string;
  description?: string;
  statuses: StatusInput[];
}

export interface ValidateResult {
  requiresMapping: boolean;
  removedStatuses: { statusId: string; name: string; issueCount: number }[];
  affectedProjects: { projectId: string; name: string; projectKey: string; affectedIssueCount: number }[];
}

export interface ApplyWorkflowData {
  name?: string;
  description?: string;
  statuses: StatusInput[];
  statusMapping: { fromStatusId: string; toStatusId: string }[];
}

// --- API calls ---

export async function fetchWorkflows() {
  const res = await api.get<ApiResponse<WorkflowDetail[]>>('/workflows');
  return res.data.data!;
}

export async function fetchWorkflow(workflowId: string) {
  const res = await api.get<ApiResponse<WorkflowDetail>>(`/workflows/${workflowId}`);
  return res.data.data!;
}

export async function createWorkflow(data: CreateWorkflowData) {
  const res = await api.post<ApiResponse<WorkflowDetail>>('/workflows', data);
  return res.data.data!;
}

export async function updateWorkflow(workflowId: string, data: UpdateWorkflowData) {
  const res = await api.put<ApiResponse<WorkflowDetail>>(`/workflows/${workflowId}`, data);
  return res.data.data!;
}

export async function validateWorkflowUpdate(workflowId: string, data: { statuses: StatusInput[] }) {
  const res = await api.put<ApiResponse<ValidateResult>>(`/workflows/${workflowId}/validate`, data);
  return res.data.data!;
}

export async function applyWorkflowUpdate(workflowId: string, data: ApplyWorkflowData) {
  const res = await api.put<ApiResponse<WorkflowDetail>>(`/workflows/${workflowId}/apply`, data);
  return res.data.data!;
}

export async function deleteWorkflow(workflowId: string) {
  await api.delete(`/workflows/${workflowId}`);
}

// --- Workflow Transition Rules (CHG-043: REQ-V43-010) ---

export type TransitionConditionType = 'REQUIRED_ROLES' | 'REQUIRED_FIELDS';

export interface TransitionCondition {
  conditionId?: string;
  conditionType: TransitionConditionType;
  conditionValue: { roles?: string[]; fields?: string[] };
}

export interface WorkflowTransition {
  transitionId: string;
  name: string;
  fromStatusId: string | null;
  toStatusId: string;
  sortOrder: number;
  conditions: TransitionCondition[];
}

export type WorkflowTransitionMode = 'FREE' | 'RESTRICTED';

export interface WorkflowStatusWithPosition extends WorkflowStatus {
  positionX: number;
  positionY: number;
}

export interface WorkflowTransitionsData {
  transitionMode: WorkflowTransitionMode;
  statuses: WorkflowStatusWithPosition[];
  transitions: WorkflowTransition[];
}

export interface SaveTransitionsData {
  transitionMode: WorkflowTransitionMode;
  statusPositions: { statusId: string; positionX: number; positionY: number }[];
  transitions: {
    transitionId?: string | null;
    name: string;
    fromStatusId: string | null;
    toStatusId: string;
    sortOrder: number;
    conditions: { conditionType: TransitionConditionType; conditionValue: Record<string, unknown> }[];
  }[];
}

export async function fetchWorkflowTransitions(workflowId: string) {
  const res = await api.get<ApiResponse<WorkflowTransitionsData>>(`/workflows/${workflowId}/transitions`);
  return res.data.data!;
}

export async function saveWorkflowTransitions(workflowId: string, data: SaveTransitionsData) {
  const res = await api.put<ApiResponse<WorkflowTransitionsData>>(`/workflows/${workflowId}/transitions`, data);
  return res.data.data!;
}

// --- Project Workflow Migration (REQ-V41-009) ---

export interface WorkflowPreviewResult {
  currentWorkflow: {
    workflowId: string;
    name: string;
    statuses: WorkflowStatus[];
  };
  targetWorkflow: {
    workflowId: string;
    name: string;
    statuses: WorkflowStatus[];
  };
  statusDistribution: { statusId: string; name: string; issueCount: number }[];
  suggestedMapping: { fromStatusId: string; toStatusId: string; reason: string }[];
}

export interface WorkflowMigrateResult {
  projectId: string;
  targetWorkflowId: string;
  migratedIssueCount: number;
}

export async function previewWorkflowChange(projectId: string, targetWorkflowId: string) {
  const res = await api.post<ApiResponse<WorkflowPreviewResult>>(
    `/projects/${projectId}/workflow/preview`,
    { targetWorkflowId },
  );
  return res.data.data!;
}

export async function migrateProjectWorkflow(
  projectId: string,
  data: { targetWorkflowId: string; statusMapping: { fromStatusId: string; toStatusId: string }[] },
) {
  const res = await api.post<ApiResponse<WorkflowMigrateResult>>(
    `/projects/${projectId}/workflow/migrate`,
    data,
  );
  return res.data.data!;
}

// --- IssueType Workflow Mapping (CHG-042: REQ-V42-003, REQ-V42-004) ---

export type IssueType = 'BUG' | 'TASK' | 'STORY' | 'EPIC' | 'SUB_TASK';

export interface IssueTypeWorkflowMappingResult {
  defaultWorkflow: {
    workflowId: string;
    name: string;
    statuses: WorkflowStatus[];
  };
  issueTypeWorkflows: {
    issueType: string;
    workflow: {
      workflowId: string;
      name: string;
      statuses: WorkflowStatus[];
    };
  }[];
  unmappedIssueTypes: string[];
}

export interface IssueTypeMappingPreviewResult {
  changes: {
    issueType: string;
    currentWorkflow: { workflowId: string; name: string; statuses: WorkflowStatus[] };
    targetWorkflow: { workflowId: string; name: string; statuses: WorkflowStatus[] };
    statusDistribution: { statusId: string; name: string; issueCount: number }[];
    suggestedMapping: { fromStatusId: string; toStatusId: string; reason: string }[];
    requiresMapping: boolean;
  }[];
  noChangeTypes: string[];
}

export interface IssueTypeMappingMigrateResult {
  projectId: string;
  totalMigratedIssueCount: number;
  details: { issueType: string; migratedCount: number }[];
}

export async function fetchIssueTypeWorkflowMappings(projectId: string) {
  const res = await api.get<ApiResponse<IssueTypeWorkflowMappingResult>>(
    `/projects/${projectId}/workflow-mappings`,
  );
  return res.data.data!;
}

export async function setIssueTypeWorkflowMappings(
  projectId: string,
  mappings: { issueType: string; workflowId: string }[],
) {
  const res = await api.put<ApiResponse<IssueTypeWorkflowMappingResult>>(
    `/projects/${projectId}/workflow-mappings`,
    { mappings },
  );
  return res.data.data!;
}

export async function previewIssueTypeWorkflowChange(
  projectId: string,
  mappings: { issueType: string; workflowId: string }[],
) {
  const res = await api.post<ApiResponse<IssueTypeMappingPreviewResult>>(
    `/projects/${projectId}/workflow-mappings/preview`,
    { mappings },
  );
  return res.data.data!;
}

export async function migrateIssueTypeWorkflows(
  projectId: string,
  data: {
    mappings: { issueType: string; workflowId: string }[];
    statusMigrations: {
      issueType: string;
      statusMapping: { fromStatusId: string; toStatusId: string }[];
    }[];
  },
) {
  const res = await api.post<ApiResponse<IssueTypeMappingMigrateResult>>(
    `/projects/${projectId}/workflow-mappings/migrate`,
    data,
  );
  return res.data.data!;
}
